/**
 * Kabuk eylem çerçeveleri (WP 2.8 §25–§27):
 * - QuickAdd: işleyici kayıt defteri. Yalnız gerçekten kullanılabilir oluşturma eylemleri gösterilir (yetenek + plan
 *   kapısı); hiç işleyici yoksa kontrol render EDİLMEZ (WP 2.9: Şirket/Kişi; Ürün oluşturma WP 2.10).
 * - GlobalSearch: WP 2.11'de gerçek arama ucuna bağlı (framework/search/GlobalSearch.tsx); buradan yeniden dışa aktarılır.
 * - PlanGate: özellik kararı arka uçtan gelir (adaptör). Karar bilinmiyorsa özellik AÇILMAZ; API `PLAN_LIMIT` yanıtı
 *   açıklayıcı duruma çevrilir. Yetenek (capability) ile plan ayrı kavramlardır.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { Building2, CheckSquare, Contact, Lock, Package, Plus } from 'lucide-react';
import { EmptyState, Menu, Skeleton, cn } from '@castinflow/ui';
import { ApiError } from '../lib/api';
import { shellStrings as trf } from './shell-strings';

// ---------------------------------------------------------------------------
// QuickAdd
// ---------------------------------------------------------------------------
export interface QuickAddHandler {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Eylemi görmek için gereken yeteneklerden en az biri (sunucunun `me.capabilities` listesi) */
  requires: readonly string[];
  /** Plan özelliği (PlanGate adaptörü); verilirse karar `enabled` olmalı */
  feature?: string;
  /** Oluşturma arayüzünü açar (modal/çekmece); başarı yalnız sunucu onayından sonra bildirilir */
  open: () => void;
}

const registry = new Map<string, QuickAddHandler>();
/** Modüller oluşturma eylemlerini kaydeder (WP 2.9+). Aynı kimlik yeniden kaydedilirse güncellenir. */
export function registerQuickAdd(h: QuickAddHandler): () => void {
  registry.set(h.id, h);
  return () => {
    registry.delete(h.id);
  };
}
export function quickAddHandlers(): QuickAddHandler[] {
  return [...registry.values()];
}

/**
 * WP 2.9: Şirket ve Kişi oluşturma. İşleyici kabuk paketindedir (form kodu değil): liste rotasına `?yeni=1` ile gider,
 * form tembel kayıt parçasında açılır. Yetki: kayıt yazma (sunucu ayrıca uygular).
 */
export function coreQuickAddHandlers(
  go: (href: string) => void,
  opts: { productQuickCreate?: boolean } = {},
): QuickAddHandler[] {
  const WRITE = ['records.write', 'records.write.lite'] as const;
  return [
    {
      id: 'company',
      label: trf.quickAdd.company,
      icon: <Building2 size={16} aria-hidden="true" />,
      requires: WRITE,
      open: () => {
        go('/records/company?yeni=1');
      },
    },
    {
      id: 'contact',
      label: trf.quickAdd.contact,
      icon: <Contact size={16} aria-hidden="true" />,
      requires: WRITE,
      open: () => {
        go('/records/contact?yeni=1');
      },
    },
    // M4 WP 4.2 (keşif O33): aktivite
    {
      id: 'activity',
      label: trf.quickAdd.activity,
      icon: <CheckSquare size={16} aria-hidden="true" />,
      requires: WRITE,
      open: () => {
        go('/records/activity?yeni=1');
      },
    },
    // WP 2.10: "Hızlı ürün kaydı" ayarı (F20 `D`, varsayılan açık) kapalıysa gösterilmez
    ...(opts.productQuickCreate === false
      ? []
      : [
          {
            id: 'product',
            label: trf.quickAdd.product,
            icon: <Package size={16} aria-hidden="true" />,
            requires: WRITE,
            open: () => {
              go('/records/product?yeni=1');
            },
          },
        ]),
  ];
}
export function availableQuickAdd(
  handlers: readonly QuickAddHandler[],
  capabilities: readonly string[],
  plan: PlanAdapter,
): QuickAddHandler[] {
  const caps = new Set(capabilities);
  return handlers.filter(
    (h) =>
      h.requires.some((c) => caps.has(c)) &&
      (h.feature === undefined || plan.decide(h.feature) === 'enabled'),
  );
}

export function QuickAdd({
  capabilities,
  handlers = quickAddHandlers(),
}: {
  capabilities: readonly string[];
  handlers?: readonly QuickAddHandler[];
}) {
  const plan = usePlan();
  const items = availableQuickAdd(handlers, capabilities, plan);
  if (items.length === 0) return null; // gerçek işleyici yok → kontrol yok (sahte eylem yok)
  return (
    <Menu
      label={trf.quickAdd.label}
      triggerTestId="quick-add"
      // Tema uyarlaması: hızlı ekle üst barda yuvarlak birincil düğme (en sık eylem; beyaz simge ≥ 4,5:1)
      triggerClassName="rounded-pill"
      trigger={
        <span className="inline-flex h-control w-control items-center justify-center rounded-pill bg-primary text-primary-foreground shadow-card transition-[background-color,transform] duration-fast hover:bg-primary-hover motion-safe:active:scale-95">
          <Plus size={18} aria-hidden="true" />
        </span>
      }
      items={items.map((h) => ({ id: h.id, label: h.label, icon: h.icon, onSelect: h.open }))}
    />
  );
}

// ---------------------------------------------------------------------------
// GlobalSearch (WP 2.11: gerçek arama ucu; bileşen framework/search/GlobalSearch.tsx)
// ---------------------------------------------------------------------------
export { GlobalSearch } from './search/GlobalSearch';

// ---------------------------------------------------------------------------
// PlanGate
// ---------------------------------------------------------------------------
/** `pending`: plan yanıtı bekleniyor (M6 WP 6.1; `PlanGate` yer tutucu gösterir, içerik açılmaz). */
export type PlanDecision = 'enabled' | 'disabled' | 'unknown' | 'pending';
export interface PlanAdapter {
  decide: (feature: string) => PlanDecision;
}
/** Sağlayıcı bağlı değilken (ör. oturum dışı): her özellik `unknown` → açılmaz (kapalı-güvenli). */
export const noPlanContract: PlanAdapter = { decide: () => 'unknown' };
const PlanContext = createContext<PlanAdapter>(noPlanContract);
export const PlanProvider = PlanContext.Provider;
export function usePlan(): PlanAdapter {
  return useContext(PlanContext);
}

/** API hatası plan sınırı mı (403 `PLAN_LIMIT` veya 402)? Yetki (FORBIDDEN) ile karıştırılmaz. */
export function isPlanLimitError(e: unknown): boolean {
  return e instanceof ApiError && (e.code === 'PLAN_LIMIT' || e.status === 402);
}

export function PlanGate({
  feature,
  children,
  error,
}: {
  feature: string;
  children: ReactNode;
  /** İlgili API çağrısının hatası: plan sınırıysa açıklayıcı durum gösterilir */
  error?: unknown;
}) {
  const plan = usePlan();
  const decision = plan.decide(feature);
  if (decision === 'enabled' && !isPlanLimitError(error)) return <>{children}</>;
  if (decision === 'pending' && !isPlanLimitError(error))
    return (
      <div data-testid="plan-gate" data-decision="pending">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  const unknown = decision === 'unknown' && !isPlanLimitError(error);
  return (
    <div
      data-testid="plan-gate"
      data-decision={decision}
      className={cn('rounded border border-border p-4')}
    >
      <EmptyState
        icon={<Lock size={18} />}
        title={unknown ? trf.plan.unknownTitle : trf.plan.title}
        description={unknown ? trf.plan.unknownBody : trf.plan.body}
        headingLevel={3}
      />
    </div>
  );
}
